/**
 * Frontend configuration for API connectivity
 *
 * runtime-config.js est chargé avant l'app (voir index.html) :
 * - Dev : servi par un plugin Vite (vite.config.ts) depuis VITE_AGENT_URL
 * - Prod : servi inline par nginx (docker/sandbox/default.conf.template) depuis AGENT_DOMAIN
 */

interface RuntimeConfig {
  AGENT_DOMAIN: string;
  AGENT_URL: string;
  SENTRY_DSN?: string;
  SENTRY_ENVIRONMENT?: string;
  SENTRY_RELEASE?: string;
  SENTRY_TRACES_SAMPLE_RATE?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * URL du backend, TOUJOURS fournie par `runtime-config.js` (jamais codée en dur ici :
 * un domaine deviné ferait taper l'app à côté en silence, ce qui est plus difficile à
 * diagnostiquer qu'un échec net). `${...}` = littéral non substitué par envsubst, donc
 * config absente elle aussi.
 */
function resolveApiUrl(): string {
  const url = window.__RUNTIME_CONFIG__?.AGENT_URL;
  if (!url || url.startsWith('${')) {
    throw new Error(
      "Configuration runtime absente : AGENT_URL n'est pas défini dans runtime-config.js. " +
        'En dev, vérifier VITE_AGENT_URL (docker-compose.override.yml) ; ' +
        'en prod, AGENT_DOMAIN (docker/sandbox/default.conf.template).',
    );
  }
  return url;
}

const API_URL = resolveApiUrl();
export const SENTRY_DSN = window.__RUNTIME_CONFIG__?.SENTRY_DSN || '';
export const SENTRY_ENVIRONMENT = window.__RUNTIME_CONFIG__?.SENTRY_ENVIRONMENT || 'development';
export const SENTRY_RELEASE = window.__RUNTIME_CONFIG__?.SENTRY_RELEASE || '';
export const SENTRY_TRACES_SAMPLE_RATE = parseFloat(
  window.__RUNTIME_CONFIG__?.SENTRY_TRACES_SAMPLE_RATE || '0.01',
);

export const config = {
  api: {
    baseURL: `${API_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 seconds
  },

  // WebSocket configuration for real-time features
  ws: {
    url: API_URL.replace(/^http/, 'ws') + '/ws',
    reconnectInterval: 5000,
    maxReconnectAttempts: 10,
  },
};

// Pour les appels API authentifiés, utiliser `authFetch` depuis `@ggvie-consulting/auth/api`.
// Helper `apiCall` retiré : il faisait du fetch brut sans header Authorization.

// Export for use in components
export default config;
